/**
 * Tail census — the instrument for "points pinned to the tail that are coat
 * and body" (Allen's second T2 field read, 2026-09-22). ONE responsibility:
 * over EVERY skinned mesh under the subject (ghosts and overlays included —
 * they render too), count what the tail bones actually move, measured through
 * three's own skinning (`getVertexPosition`), and set it against the zone.
 *
 * Two numbers per mesh, because they can disagree and the disagreement is
 * the finding: `weighted` — vertices whose skin arrays name a tail bone
 * (what the weight view reads), and `moved` — vertices that actually move
 * when every tail bone turns (what the eye sees). `outside` counts either
 * kind that is NOT a zone vertex.
 *
 * Read-only: the bones are turned and put back within the call.
 * Depends on: three, adapter-three (debris identity). Depended on by: main's
 * e2e probe.
 */
import { Quaternion, Vector3, type Mesh, type Object3D, type SkinnedMesh } from 'three';
import { viewportDebrisLabel } from '@harryhausen/adapter-three';

export interface TailCensusMesh {
  readonly name: string;
  /** Viewport debris label (a ghost/overlay), or null for the subject's own mesh. */
  readonly debris: string | null;
  readonly vertices: number;
  /** Vertices whose skin names a tail bone. */
  readonly weighted: number;
  /** Vertices that move > 0.1 mm when every tail bone turns 20°. */
  readonly moved: number;
  /** Of `weighted`, how many are not zone vertices (only meaningful on the subject mesh). */
  readonly weightedOutside: number;
  /** Of `moved`, how many are not zone vertices. */
  readonly movedOutside: number;
}

const TAIL = /^Tail\d+(_graft)?$/;

export function tailCensus(root: Object3D, zoneVertices: ReadonlySet<number>): TailCensusMesh[] {
  const meshes: SkinnedMesh[] = [];
  root.traverse((n) => {
    if ((n as SkinnedMesh).isSkinnedMesh) meshes.push(n as SkinnedMesh);
  });
  const tailBones = new Set<Object3D>();
  for (const m of meshes) for (const b of m.skeleton.bones) if (TAIL.test(b.name)) tailBones.add(b);
  const turn = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), (20 * Math.PI) / 180);
  root.updateMatrixWorld(true);
  const before = meshes.map((m) => {
    const count = m.geometry.getAttribute('position').count;
    return Array.from({ length: count }, (_, i) => m.getVertexPosition(i, new Vector3()));
  });
  const saved = [...tailBones].map((b) => b.quaternion.clone());
  for (const b of tailBones) b.quaternion.multiply(turn);
  root.updateMatrixWorld(true);
  // A face ghost (the zone tint) is a NON-INDEXED copy of the subject: ghost
  // vertex i IS subject vertex index[i], so its census reads against that.
  const subject = meshes.find((m) => viewportDebrisLabel(m) === null);
  const subjectIndex = subject?.geometry.getIndex() ?? null;
  const out: TailCensusMesh[] = meshes.map((m, mi) => {
    const si = m.geometry.getAttribute('skinIndex');
    const sw = m.geometry.getAttribute('skinWeight');
    const count = m.geometry.getAttribute('position').count;
    const unrolled = m !== subject && subjectIndex !== null && m.geometry.getIndex() === null && count === subjectIndex.count;
    const source = (v: number): number => (unrolled ? subjectIndex!.getX(v) : v);
    let weighted = 0;
    let moved = 0;
    let weightedOutside = 0;
    let movedOutside = 0;
    const p = new Vector3();
    for (let v = 0; v < count; v++) {
      let w = 0;
      for (let k = 0; k < 4; k++) {
        const bone = m.skeleton.bones[si.getComponent(v, k)];
        if (bone !== undefined && TAIL.test(bone.name)) w += sw.getComponent(v, k);
      }
      if (w > 0) {
        weighted++;
        if (!zoneVertices.has(source(v))) weightedOutside++;
      }
      if (m.getVertexPosition(v, p).distanceTo(before[mi]![v]!) > 1e-4) {
        moved++;
        if (!zoneVertices.has(source(v))) movedOutside++;
      }
    }
    return { name: m.name, debris: viewportDebrisLabel(m), vertices: count, weighted, moved, weightedOutside, movedOutside };
  });
  [...tailBones].forEach((b, i) => b.quaternion.copy(saved[i]!));
  root.updateMatrixWorld(true);
  return out;
}

export interface SpringCensusMesh {
  readonly name: string;
  readonly kind: 'skinned' | 'mesh' | 'line' | 'points';
  /** Viewport debris label, or null for a mesh of the character itself. */
  readonly debris: string | null;
  /** Drawn right now (the mesh and every ancestor visible). */
  readonly visible: boolean;
  readonly skeleton: string | null;
  /** How many of its skeleton's bones ARE the spring's bone objects (identity, not name). */
  readonly bindsSpringBones: number;
  /** Tail-named bones in its skeleton that are NOT the spring's objects (a clone). */
  readonly foreignTailBones: number;
  /** Vertices whose skin names a tail bone. */
  readonly weighted: number;
  /** Vertices that move > 0.1 mm when ONLY the spring's bones turn 20°. */
  readonly moved: number;
  /** Vertices whose BIND position sits inside the tail's tube (Tail2 on, 1.2 × the flesh radius) — flesh that IS tail. */
  readonly inTube: number;
  /** Of `inTube`, how many do not move with the spring — a tail that stays behind. */
  readonly inTubeStatic: number;
  /** The static in-tube vertices by the chain segment they sit on (Tail2→Tail3 first) — where the stay-behind flesh is. */
  readonly staticBySegment: readonly number[];
  /** Their dominant bones, most first — who owns the flesh that stays behind. */
  readonly staticOwners: readonly { readonly bone: string; readonly vertices: number }[];
}

function distanceToSegment(p: Vector3, a: Vector3, b: Vector3): number {
  const ab = b.clone().sub(a);
  const t = Math.min(1, Math.max(0, p.clone().sub(a).dot(ab) / Math.max(ab.lengthSq(), 1e-12)));
  return p.distanceTo(a.clone().addScaledVector(ab, t));
}

/**
 * ADR-038 — who draws the tail, and does it follow the SPRING? Every mesh
 * under `stage` (the whole viewport scene: the subject, the zone-tint ghost,
 * the weight-view ghost, anything else), measured against the spring's own
 * bone OBJECTS: a mesh bound to a clone of the chain, or a copy of the tail
 * flesh bound to nothing that springs, shows up as `inTubeStatic > 0`.
 * The tube is the chain at BIND (Tail2 on, 1.2 × the flesh radius there),
 * read through each mesh's own skeleton. Restores every quaternion it turns.
 */
export function springCensus(
  stage: Object3D,
  springBones: readonly Object3D[],
  radii: readonly number[],
): SpringCensusMesh[] {
  // Everything that draws geometry: meshes, lines (a wireframe) and points.
  const meshes: Mesh[] = [];
  stage.traverse((n) => {
    const g = (n as Mesh).geometry as Mesh['geometry'] | undefined;
    if (g?.getAttribute('position') !== undefined) meshes.push(n as Mesh);
  });
  const kindOf = (m: Mesh): SpringCensusMesh['kind'] => {
    const o = m as unknown as { isSkinnedMesh?: boolean; isLine?: boolean; isPoints?: boolean };
    return o.isSkinnedMesh === true ? 'skinned' : o.isLine === true ? 'line' : o.isPoints === true ? 'points' : 'mesh';
  };
  const spring = new Set(springBones);
  const visibleNow = (o: Object3D): boolean => {
    for (let n: Object3D | null = o; n !== null; n = n.parent) if (!n.visible) return false;
    return true;
  };
  const positions = (m: Mesh): Vector3[] => {
    const count = m.geometry.getAttribute('position').count;
    const attr = m.geometry.getAttribute('position');
    return Array.from({ length: count }, (_, i) =>
      (kindOf(m) === 'skinned' || kindOf(m) === 'mesh' ? m.getVertexPosition(i, new Vector3()) : new Vector3().fromBufferAttribute(attr, i)).applyMatrix4(m.matrixWorld),
    );
  };
  stage.updateMatrixWorld(true);
  const before = meshes.map(positions);
  const saved = springBones.map((b) => b.quaternion.clone());
  const turn = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), (20 * Math.PI) / 180);
  for (const b of springBones) b.quaternion.multiply(turn);
  stage.updateMatrixWorld(true);
  const after = meshes.map(positions);
  springBones.forEach((b, k) => b.quaternion.copy(saved[k]!));
  stage.updateMatrixWorld(true);
  return meshes.map((m, mi) => {
    const skinned = (m as SkinnedMesh).isSkinnedMesh === true ? (m as SkinnedMesh) : null;
    const bones = skinned?.skeleton.bones ?? [];
    let weighted = 0;
    let moved = 0;
    let inTube = 0;
    let inTubeStatic = 0;
    // Bind-space geometry for a skinned mesh: vertex = matrixWorld · position;
    // a bone = matrixWorld · bindMatrixInverse · boneInverse⁻¹ · origin.
    const bindTube = ((): { a: Vector3; b: Vector3; r: number }[] | null => {
      if (skinned === null) return null;
      const at = springBones.map((sb) => {
        const i = bones.indexOf(sb as never);
        if (i < 0) return null;
        return new Vector3().applyMatrix4(skinned.skeleton.boneInverses[i]!.clone().invert()).applyMatrix4(skinned.bindMatrixInverse).applyMatrix4(skinned.matrixWorld);
      });
      if (at.some((x) => x === null)) return null;
      const pts = at as Vector3[];
      return pts.slice(1, -1).map((a, k) => ({ a, b: pts[k + 2]!, r: 1.2 * (radii[k + 2] ?? 0.03) }));
    })();
    const pos = m.geometry.getAttribute('position');
    const bindOf = (v: number): Vector3 => new Vector3().fromBufferAttribute(pos, v).applyMatrix4(m.matrixWorld);
    const staticBySegment = (bindTube ?? []).map(() => 0);
    const owners = new Map<string, number>();
    const si = skinned?.geometry.getAttribute('skinIndex');
    const sw = skinned?.geometry.getAttribute('skinWeight');
    before[mi]!.forEach((p, v) => {
      let w = 0;
      if (si !== undefined && sw !== undefined) {
        for (let k = 0; k < 4; k++) {
          const bone = bones[si.getComponent(v, k)];
          if (bone !== undefined && TAIL.test(bone.name)) w += sw.getComponent(v, k);
        }
      }
      if (w > 0) weighted++;
      const didMove = after[mi]![v]!.distanceTo(p) > 1e-4;
      if (didMove) moved++;
      // The tube is judged at BIND: flesh that IS tail when the character stands at
      // rest — a leg the walk swings past the tail is not a second tail.
      const seg = bindTube === null ? -1 : bindTube.findIndex((s) => distanceToSegment(bindOf(v), s.a, s.b) <= s.r);
      if (seg >= 0) {
        inTube++;
        if (!didMove) {
          inTubeStatic++;
          staticBySegment[seg]! += 1;
          if (si !== undefined && sw !== undefined) {
            let best = -1;
            let bw = 0;
            for (let k = 0; k < 4; k++) if (sw.getComponent(v, k) > bw) { bw = sw.getComponent(v, k); best = si.getComponent(v, k); }
            const name = bones[best]?.name ?? '?';
            owners.set(name, (owners.get(name) ?? 0) + 1);
          }
        }
      }
    });
    return {
      name: m.name,
      kind: kindOf(m),
      debris: viewportDebrisLabel(m),
      visible: visibleNow(m),
      skeleton: skinned?.skeleton.uuid ?? null,
      bindsSpringBones: bones.filter((b) => spring.has(b)).length,
      foreignTailBones: bones.filter((b) => TAIL.test(b.name) && !spring.has(b)).length,
      weighted,
      moved,
      inTube,
      inTubeStatic,
      staticBySegment,
      staticOwners: [...owners].sort((a, b) => b[1] - a[1]).map(([bone, vertices]) => ({ bone, vertices })),
    };
  });
}
